import { useReducer, useState, useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import Papa from 'papaparse';

import Card from '@components/card';
import DragInput from '@components/drag-input';
import Select from '@components/select';
import Breadcrumb from '@src/app-components/breadcrumb';
import LoaderButton from '@src/app-components/loader/LoaderButton';

import { keyAsText } from '@src/utils';
import {
  getIsRequired,
  reduceCsvState,
  formatAsNumber,
  formatJsonKey,
  formatTelemetryJsonKey,
} from './dataUploadHelper';

import './dataupload.scss';

const breadcrumbLinks = [
  {
    text: 'Data Upload',
    current: true,
  },
];

export default connect(
  'doFetchUploadSessionLogs',
  'doUploadAllFiles',
  'selectUploadLogs',
  'selectLoadingButtonState',
  ({ doFetchUploadSessionLogs, doUploadAllFiles, uploadLogs, loadingButtonState }) => {
    const [recorder, setRecorder] = useState('');
    const [version, setVersion] = useState(null);
    const [files, setFiles] = useState({
      siteFile: null,
      searchEffortFile: null,
      telemetryFishFile: null,
      missouriRiverFile: null,
      fishFile: null,
      supplementalFile: null,
      proceduresFile: null,
    });
    const [csvData, dispatch] = useReducer(reduceCsvState, {
      siteFile: null,
      searchEffortFile: null,
      telemetryFishFile: null,
      missouriRiverFile: null,
      fishFile: null,
      supplementalFile: null,
      proceduresFile: null,
    });

    const fileKeys = Object.keys(files);

    const setFile = (key, file) => {
      setFiles({
        ...files,
        [key]: file,
      });

      if (file) {
        Papa.parse(file, {
          complete: (result) => dispatch({ type: 'update', key, data: result.data }),
          transformHeader: key !== 'telemetryFishFile' ? formatJsonKey : formatTelemetryJsonKey,
          transform: formatAsNumber,
          skipEmptyLines: true,
          header: true,
        });
      } else {
        dispatch({ type: 'update', key, data: null });
      }
    };

    const submitIsDisabled = () => {
      let isDisabled = false;

      fileKeys.forEach((key) => {
        if (getIsRequired(key, files)) {
          if (!files[key]) isDisabled = true;
        }
      });

      return isDisabled;
    };

    const uploadAllFiles = () => {
      doUploadAllFiles({ files, data: csvData, version, recorder });
    };

    useEffect(() => {
      doFetchUploadSessionLogs();
    }, []);

    return (
      <div className='container-fluid w-75'>
        <Breadcrumb paths={breadcrumbLinks} />
        <Card>
          <Card.Header text='File Upload' />
          <Card.Body>
            Select the version of the Field App used to generate your datasheets.
            <div className='mt-2'>
              <Select
                className='w-25 d-inline-block mb-1 mr-4'
                onChange={(value) => setVersion(value)}
                defaultOption={{ value: '4.2.1' }}
                options={[{ value: '4.2.2' }, { value: '4.2.1' }, { value: '4.1.3' }]}
              />
              <label>
                <small>Recorder:</small>
              </label>
              <input
                type='text'
                placeholder='Initials...'
                className='form-control d-inline-block ml-2'
                style={{ width: '75px' }}
                value={recorder}
                onChange={(e) => setRecorder(e.target.value)}
              />
            </div>
            {version && (
              <>
                <hr />
                <p>Upload files to each of the required fields denoted by an asterisk (*):</p>
                {fileKeys.map((key) => {
                  const isRequired = getIsRequired(key, files);

                  return (
                    <div key={key} className='row'>
                      <div className='col-md-2 pt-2'>
                        <p className='pt-1'>
                          {isRequired ? '* ' : ''}
                          {keyAsText(key)}:
                        </p>
                      </div>
                      <div className='col-md-10'>
                        <DragInput onChange={(file) => setFile(key, file)} />
                      </div>
                    </div>
                  );
                })}
                <hr />
                <LoaderButton
                  isOutline
                  title='Submit Files'
                  onClick={() => uploadAllFiles()}
                  isDisabled={submitIsDisabled()}
                  isLoading={loadingButtonState}
                >
                  Submit Files
                </LoaderButton>
              </>
            )}
          </Card.Body>
        </Card>
        <Card className='mt-3'>
          <Card.Header text='Upload Session Logs' />
          <Card.Body>
            {uploadLogs.length ? (
              <>
                <div>{`Date Created: ${uploadLogs[0].dateCreated.split('T')[0]}`}</div>
                {uploadLogs.map((log, index) => (
                  <div key={index} className='text log'>
                    {log.debugText}
                  </div>
                ))}
              </>
            ) : (
              <div className='text'>No logs to report</div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
);
